import type React from 'react';
import { useAvatarStore } from '@features/avatar-integration';
import { getAvatarStatusLabel } from '@entities/avatar';
import { Panel, Toggle, RadioOption, InfoIcon } from '@shared/ui';
import { OverlayOrderList } from './OverlayOrderList';
import type { AvatarOutputMode } from '@shared/types';

const OUTPUT_MODE_OPTIONS: { value: AvatarOutputMode; label: string }[] = [
  { value: 'local-preview', label: 'Local Preview' },
  { value: 'stream-output', label: 'Stream Output' },
  { value: 'both', label: 'Both' },
];

export function AvatarIntegrationPanel() {
  const integration = useAvatarStore((state) => state.integration);
  const setOverlayEnabled = useAvatarStore((state) => state.setOverlayEnabled);
  const setOutputMode = useAvatarStore((state) => state.setOutputMode);

  return (
    <Panel
      title="AVATAR INTEGRATION"
      titleAccessory={<InfoIcon className="h-4 w-4 text-text-muted" />}
    >
      <FieldRow label="Status">
        <StatusPill label={getAvatarStatusLabel(integration.status)} />
      </FieldRow>

      <FieldRow label="Renderer">
        <ReadOnlyField value="Unity" />
      </FieldRow>

      <FieldRow label="Overlay">
        <Toggle
          checked={integration.overlayEnabled}
          onChange={setOverlayEnabled}
          label={integration.overlayEnabled ? 'Enabled' : 'Disabled'}
        />
      </FieldRow>

      <div className="my-5 h-px bg-surface-border" />

      <div className="mb-5">
        <div className="mb-3 text-sm text-text-secondary">Avatar Output</div>
        <div className="flex flex-col gap-3">
          {OUTPUT_MODE_OPTIONS.map((option) => (
            <RadioOption
              key={option.value}
              selected={integration.outputMode === option.value}
              onSelect={() => setOutputMode(option.value)}
              label={option.label}
            />
          ))}
        </div>
      </div>

      <div className="my-5 h-px bg-surface-border" />

      <div>
        <div className="mb-3 text-sm text-text-secondary">Overlay Order</div>
        <OverlayOrderList />
      </div>
    </Panel>
  );
}

function FieldRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="mb-4 flex items-center justify-between">
      <span className="text-sm text-text-secondary">{label}</span>
      {children}
    </div>
  );
}

function StatusPill({ label }: { label: string }) {
  return (
    <span className="flex items-center gap-2 rounded-md border border-surface-border bg-surface-2 px-3 py-1.5 text-sm text-text-primary">
      <span className="h-2 w-2 rounded-full border border-text-muted" />
      {label}
    </span>
  );
}

function ReadOnlyField({ value }: { value: string }) {
  return (
    <span className="rounded-md border border-surface-border bg-surface-2 px-3 py-1.5 text-sm text-text-primary">
      {value}
    </span>
  );
}
